import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Calendar,
  ExternalLink,
  Instagram,
  Send,
} from "lucide-react";
import { Navigation } from "./Navigation";
import ContactButton from "./ContactButton";
import ContactModal from "./ContactModal";
import BookingForm from "./BookingForm";
import CalWidget from "./CalWidget";
import SEO, {
  generateContactPageSchema,
  generateBreadcrumbSchema,
} from "./SEO";
import Footer from "./Footer";

export default function ContactPage() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isCalOpen, setIsCalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Use Netlify's form handling instead of direct fetch
      const formDataToSend = new FormData();
      formDataToSend.append("form-name", "contact");
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("message", formData.message);

      const response = await fetch("/.netlify/functions/contact-form", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    url: "https://one80labs.com",
    telephone: "+1-402-915-3616",
    email: "hello@one80labs.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651675,
      longitude: -95.9397187,
    },
    openingHours: "Mo-Fr 09:00-17:00",
    sameAs: ["https://www.instagram.com/one80labs/"],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+1-402-915-3616",
        contactType: "customer service",
        areaServed: "US",
        availableLanguage: "English",
        hoursAvailable: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "17:00",
        },
      },
      {
        "@type": "ContactPoint",
        telephone: "+1-402-915-3616",
        contactType: "sales",
        areaServed: "US",
        availableLanguage: "English",
        hoursAvailable: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "17:00",
        },
      },
      {
        "@type": "ContactPoint",
        email: "hello@one80labs.com",
        contactType: "customer service",
        areaServed: "US",
        availableLanguage: "English",
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What services does One80Labs offer?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "One80Labs offers AI training, fractional project management, and custom AI tool development. We specialize in helping non-profits and businesses in Omaha and the Midwest leverage AI technology to improve operations and increase impact.",
        },
      },
      {
        "@type": "Question",
        name: "How can I schedule a consultation?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "You can schedule a 30-minute consultation by clicking the 'Schedule Consultation' button on our contact page, which will open our Cal.com booking system. You can also call us at (402) 915-3616 or email us at hello@one80labs.com.",
        },
      },
      {
        "@type": "Question",
        name: "What are your business hours?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Our business hours are Monday through Friday, 9:00 AM to 5:00 PM Central Time. We're located in Omaha, Nebraska and serve clients throughout the Midwest.",
        },
      },
      {
        "@type": "Question",
        name: "Do you work with non-profits?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, we specialize in helping non-profits leverage AI technology to improve operations, increase impact, and better serve their communities. We have extensive experience working with non-profit organizations in Omaha and throughout the Midwest.",
        },
      },
      {
        "@type": "Question",
        name: "What is fractional project management?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Fractional project management provides experienced project management expertise on a part-time or as-needed basis. This allows organizations to access senior-level project management skills without the cost of a full-time hire, perfect for growing businesses and non-profits.",
        },
      },
    ],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://one80labs.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Contact",
        item: "https://one80labs.com/contact",
      },
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "One80Labs",
    url: "https://one80labs.com",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://one80labs.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "One80Labs",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    url: "https://one80labs.com",
    logo: "https://one80labs.com/logo.png",
    foundingDate: "2024",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-402-915-3616",
      contactType: "customer service",
      email: "hello@one80labs.com",
      areaServed: "US",
    },
    sameAs: ["https://www.instagram.com/one80labs/"],
    founder: {
      "@type": "Person",
      name: "Nick Kramer",
      jobTitle: "AI Productivity Specialist & Technology Educator",
      image:
        "https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png",
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Contact One80Labs Omaha | AI Training & Fractional PM Services"
        description="Contact One80Labs in Omaha, Nebraska for AI training, fractional project management, and custom AI tools. Serving Council Bluffs and the Midwest. Call (402) 915-3616 or schedule online."
        jsonLd={[generateContactPageSchema()]}
        breadcrumbs={[
          { name: "Home", url: "https://one80labs.com" },
          { name: "Contact", url: "https://one80labs.com/contact" },
        ]}
        keywords={[
          "contact One80Labs",
          "AI training Omaha",
          "fractional project management Nebraska",
          "AI consulting Council Bluffs",
          "Omaha AI services",
          "Midwest AI training",
          "AI tools development",
          "project management Omaha",
          "AI implementation Nebraska",
          "business AI consulting",
        ]}
        image="https://one80labs.com/logo.png"
        article={false}
        author="One80Labs"
        publishedTime=""
        modifiedTime=""
        section="Contact"
        tags={[
          "contact",
          "consultation",
          "AI training",
          "fractional PM",
          "Omaha",
          "Nebraska",
          "Council Bluffs",
          "Midwest",
          "AI consulting",
          "project management",
        ]}
        localSeo={{
          businessName: "One80Labs",
          address: "4383 Nicholas Street #302, Omaha, NE 68181",
          phone: "(402) 915-3616",
          email: "hello@one80labs.com",
          coordinates: { lat: 41.2651675, lng: -95.9397187 },
        }}
      />

      <Navigation />

      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1
              className="text-4xl font-bold text-gray-900 mb-4"
              id="main-heading"
            >
              Get in Touch
            </h1>
            <p
              className="text-xl text-gray-600 max-w-3xl mx-auto"
              role="doc-subtitle"
            >
              We'd love to hear from you. Whether you're curious about{" "}
              <Link
                to="/fractional-pm"
                className="text-[#F98B3D] hover:underline"
              >
                fractional PM services
              </Link>
              ,{" "}
              <Link to="/courses" className="text-[#F98B3D] hover:underline">
                AI training
              </Link>
              , or need a{" "}
              <Link to="/about" className="text-[#F98B3D] hover:underline">
                custom AI tool
              </Link>
              — we're here to help.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form & Info Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2
              className="text-2xl font-semibold text-gray-900 mb-6"
              id="contact-form"
            >
              Send us a Message
            </h2>

            {submitStatus === "success" && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-800">
                  Thank you for your message! We'll get back to you soon.
                </p>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800">
                  Sorry, there was an error sending your message. Please try
                  again or contact us directly.
                </p>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              role="form"
              aria-labelledby="contact-form"
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="contact" />
              <div hidden>
                <input name="bot-field" />
              </div>
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  aria-describedby="name-help"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                />
                <div id="name-help" className="sr-only">
                  Enter your full name
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  aria-describedby="email-help"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                />
                <div id="email-help" className="sr-only">
                  Enter your email address
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  aria-required="true"
                  aria-describedby="message-help"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F98B3D] focus:border-[#F98B3D]"
                  placeholder="Tell us about your project, questions, or how we can help..."
                />
                <div id="message-help" className="sr-only">
                  Tell us about your project, questions, or how we can help
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-5 w-5 mr-2" />
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Book Appointment */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3
                className="text-xl font-semibold text-gray-900 mb-4"
                id="book-appointment"
              >
                Book an Appointment
              </h3>
              <p className="text-gray-600 mb-4">
                Schedule a 30-minute consultation to discuss your needs.
              </p>
              <button
                onClick={() => {
                  const url = "https://cal.com/one80/30min";
                  const width = 800;
                  const height = 600;
                  const left = (window.screen.width - width) / 2;
                  const top = (window.screen.height - height) / 2;

                  window.open(
                    url,
                    "calPopup",
                    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
                  );
                }}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Schedule Consultation
              </button>
            </div>

            {/* Contact Details */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h3
                className="text-xl font-semibold text-gray-900 mb-6"
                id="contact-info"
              >
                Contact Information
              </h3>

              <div className="space-y-4">
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-[#F98B3D] mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <a
                      href="tel:+1-402-915-3616"
                      className="text-gray-900 hover:text-[#F98B3D] transition-colors"
                    >
                      (402) 915-3616
                    </a>
                  </div>
                </div>

                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-[#F98B3D] mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a
                      href="mailto:hello@one80labs.com"
                      className="text-gray-900 hover:text-[#F98B3D] transition-colors"
                    >
                      hello@one80labs.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-[#F98B3D] mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="text-gray-900">
                      4383 Nicholas Street #302
                      <br />
                      Omaha, NE 68181
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Instagram className="h-5 w-5 text-[#F98B3D] mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Follow us</p>
                    <a
                      href="https://www.instagram.com/one80labs/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-900 hover:text-[#F98B3D] transition-colors flex items-center"
                    >
                      @one80labs
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl font-bold text-gray-900 mb-8 text-center"
            id="faq-section"
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What services does One80Labs offer?
              </h3>
              <p className="text-gray-600">
                One80Labs offers AI training, fractional project management, and
                custom AI tool development. We specialize in helping non-profits
                and businesses in Omaha and the Midwest leverage AI technology
                to improve operations and increase impact.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                How can I schedule a consultation?
              </h3>
              <p className="text-gray-600">
                You can schedule a 30-minute consultation by clicking the
                "Schedule Consultation" button on our contact page, which will
                open our Cal.com booking system. You can also call us at (402)
                915-3616 or email us at hello@one80labs.com.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What are your business hours?
              </h3>
              <p className="text-gray-600">
                Our business hours are Monday through Friday, 9:00 AM to 5:00 PM
                Central Time. We're located in Omaha, Nebraska and serve clients
                throughout the Midwest.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Do you work with non-profits?
              </h3>
              <p className="text-gray-600">
                Yes, we specialize in helping non-profits leverage AI technology
                to improve operations, increase impact, and better serve their
                communities. We have extensive experience working with
                non-profit organizations in Omaha and throughout the Midwest.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What is fractional project management?
              </h3>
              <p className="text-gray-600">
                Fractional project management provides experienced project
                management expertise on a part-time or as-needed basis. This
                allows organizations to access senior-level project management
                skills without the cost of a full-time hire, perfect for growing
                businesses and non-profits.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl font-semibold text-gray-900 mb-8 text-center"
            id="location-map"
          >
            Find Us
          </h2>
          <div className="rounded-lg overflow-hidden shadow-sm">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2999.3176080142147!2d-95.9397187!3d41.2651675!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87938dc0123cde99%3A0x384d8a9c3c5b37e2!2s4383%20Nicholas%20St%20%23302%2C%20Omaha%2C%20NE%2068131!5e0!3m2!1sen!2sus!4v1709734844045!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="One80Labs Location Map"
            ></iframe>
          </div>
        </div>
      </div>

      <Footer />

      {/* Contact Components */}
      <ContactButton
        onClick={() => setIsContactModalOpen(true)}
        onCalendarClick={() => setIsBookingOpen(true)}
        onCalClick={() => setIsCalOpen(true)}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <BookingForm
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
      />
      <CalWidget isOpen={isCalOpen} onClose={() => setIsCalOpen(false)} />

      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(localBusinessSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(websiteSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema),
        }}
      />
    </div>
  );
}
